<template>
  <div class="post-webwidget-v2-player-audio-player" :class="audioPlayerClassList">
    <audio
      ref="audioPlayer"
      :autoplay="autoplay"
      @abort="onAbort"
      @canplay="onCanPlay"
      @durationchange="onDurationChange"
      @emptied="onEmptied"
      @ended="onEnded"
      @error="onError"
      @loadeddata="onLoadedData"
      @pause="onPause"
      @play="onPlay"
      @playing="onPlaying"
      @progress="onProgress"
      @seeked="onSeeked"
      @seeking="onSeeking"
      @stalled="onStalled"
      @suspend="onSuspend"
      @timeupdate="onTimeUpdate"
      @volumechange="onVolumeChange"
      @waiting="onWaiting"
    >
      <source :src="audioFileSrc" @error="onError" />
    </audio>
    <div class="post-webwidget-v2-player-audio-player-controls">
      <div class="post-webwidget-v2-player-audio-player-controls-left-side">
        <!-- D-233 (A9): v2's transport. The class is UNCHANGED on purpose — three e2e
             specs and the visual harness locate this control by it. -->
        <button
          type="button"
          class="post-webwidget-v2-player-audio-player-controls__play-toggle"
          :aria-label="playToggleLabel"
          :aria-disabled="playToggleInert ? 'true' : null"
          @click="onPlayToggleButton"
        >
          <!-- D-255 (A9): the glyph WAS the entire content of this control, so before the
               line above it was an unnamed click target; `aria-label` names it and the
               image is decoration. -->
          <img :src="playToggleButton" width="30" height="30" alt="" />
        </button>
      </div>
      <div class="post-webwidget-v2-player-audio-player-controls-right-side">
        <!-- 🔴 D-256 (A9): v2 HAD NO SCRUBBER, so this one is BUILT rather than labelled —
             the one item in A1-A9 that adds a control instead of naming one.

             🪤 THE RANGE IS AN OVERLAY, AND THE PAINTING DIV STAYS. The obvious port of
             v1/v3/v4 replaces the div with the `<input>` and repaints the fill as a
             `linear-gradient` on the input's own background. That was rejected twice
             over: the fill and track colours are SCSS variables with a whole
             `--dark-theme` override block, so a gradient would have had to move those
             colours into JS or into the tree's FIRST `var(--…)` custom property (there
             are currently zero, and `vite.config.js` targets es2015 on a widget that
             embeds on arbitrary station sites). Keeping the div means the 36 v2 goldens
             photograph the same pixels they already did.

             🔑 `__played` was ALREADY `pointer-events: none` so the bar could catch the
             click, which is what makes an overlay work at all: the range sits on top and
             takes the pointer, and a native range click positions the thumb more
             accurately than the `offsetX` arithmetic it replaces.

             🔑 The div keeps its class — it is the visual harness's mask selector for the
             two cells where the playhead genuinely moves. -->
        <div class="post-webwidget-v2-player-audio-player-progress-bar">
          <!--
           <div class="post-webwidget-v2-player-audio-player-progress-bar__buffered" :style="bufferedBarStyle"></div>
          -->
          <div class="post-webwidget-v2-player-audio-player-progress-bar__played" :style="progressBarStyle"></div>
          <!-- `aria-disabled`, not `disabled`, for A3's reason (D-234): `disabled` blurs a
               focused control, and the guard in `onSeek` is the real enforcement. This
               announces the state the handler implements. -->
          <input
            type="range"
            class="post-webwidget-v2-player-audio-player-progress-bar__range"
            min="0"
            max="100"
            step="1"
            aria-label="Audio position"
            :aria-disabled="canSeek ? null : 'true'"
            :aria-valuetext="valueText"
            :value="position"
            @input="onSeek"
          />
        </div>
        <div class="post-webwidget-v2-player-audio-player-progress-text">
          <template v-if="audioFileSrc">
            <div>
              <!-- 🔴 D-160 — the give-up needs a state of its own, because BOTH
                   branches below are lies once the engine has stopped: no data yet
                   reads "Buffering..." for the rest of the pageview, and a give-up
                   that lands mid-stream reads "LIVE" beside a Play glyph for a
                   stream that has been destroyed. `notifyUnrecoverable`'s stated
                   purpose is to let the component clear its spinner, and this is the
                   spinner it never reached.
                   🔑 Keyed on `mediaFailed && !canPlay`, not `mediaFailed` alone.
                   The latch is cleared only by `resetData()` and by the retry tap —
                   never by recovery — so on `mediaFailed` alone this would sit over
                   working audio after a transient error resolved, trading one
                   permanent lie for another. `onError` writes the two together, so
                   the pair is exactly the window in which the retry branch of
                   `onPlayToggleButton` is live. -->
              <template v-if="mediaFailed && !canPlay">
                <div>Unavailable — tap to retry</div>
              </template>
              <template v-else-if="loadedMediaData">
                <div v-if="playingLiveStream">LIVE</div>
                <div v-else>{{ formattedTimePlayed }} / {{ formattedDuration }}</div>
              </template>
              <template v-else>
                Buffering...
              </template>
            </div>
          </template>
          <template v-else>
            <div>Select an episode</div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// This file should be refactored.

import { reportNotAllowedIfApplicable, reportPlayerRetry } from '../core/faro';
import { formatDurationClock } from '../utils/dateFormat';
import formatDuration from '../utils/formatDuration';
import scrubberValueText from '../shared-components/scrubberValueText';
// LP-2 / MR-76: live audio routes through the shared hls.js engine (LP-H/MR-75)
// instead of the retired Viblast runtime global.
import { attachLiveStream, detachLiveStream } from '../utils/hls_engine';
// Vue-1 `require('...svg')` -> ES-module imports so Vite resolves the assets
// (mirrors the v3 subtree). Small SVGs inline as data URIs (assetsInlineLimit),
// so the single-IIFE dist shape + gzip budget are preserved.
import playWhiteSvg from '../../static/icons/white/svg/play.svg';
import playGreySvg from '../../static/icons/grey/svg/play.svg';
import pauseWhiteSvg from '../../static/icons/white/svg/pause.svg';
import pauseGreySvg from '../../static/icons/grey/svg/pause.svg';

export default {
  name: 'live-audio-player',
  // D-256 (A9): the same rate-limiter v1/v3/v4's ranges use. It exists because a
  // `timeupdate`-driven `aria-valuetext` on a FOCUSED slider is read aloud several times
  // a second — see that file's 🪤. v2's new range is the fourth consumer, not a variant.
  mixins: [scrubberValueText],
  // Vue3 (LP-4/MR-78, the LP-2-deferred emits item): declare the $emitted events.
  // `play`/`pause` are the component's OWN forwarded events to its parent
  // (NowPlayingDetail `@play`/`@pause`) — declaring them, exactly as the v1
  // media_player and v4 PlayPause siblings do, binds them as component events
  // rather than falling through as native attrs on the root.
  emits: ['ready', 'episode-ended', 'play', 'pause'],
  props: {
    darkTheme: Boolean,
    audioFileSrc: String,
    autoplay: {
      type: Boolean,
      default: false,
    },
    playingLiveStream: Boolean,
  },
  data() {
    return {
      loadedMediaData: false,
      fileLoadProgress: undefined,
      buffered: undefined,
      fileSize: undefined,
      isPlaying: false,
      canPlay: false,
      // D-133 — a latch, because a failed load is otherwise indistinguishable from
      // "not loaded yet": both leave canPlay false, and onPlayToggleButton treats
      // that as "do nothing". Without this the play button is permanently inert.
      mediaFailed: false,
      // D-165 — a SECOND latch, and it exists only so the retry has an outcome.
      // `mediaFailed` says "the load failed"; this says "the listener asked for
      // another go and we do not yet know how it went". The next `canplay` after
      // a retry is a recovery and the next `error` is a second failure — but
      // without this flag both are indistinguishable from the ordinary first
      // load, so the retry would have a count and no success RATE.
      retryPending: false,
      duration: undefined,
      currentTime: undefined,
    };
  },
  computed: {
    audioPlayerClassList() {
      return {
        'post-webwidget-v2-player-audio-player--dark-theme': this.darkTheme,
      };
    },

    bufferedPercentage() {
      return (this.buffered / this.duration) * 100 || 0;
    },

    bufferedBarStyle() {
      return {
        width: `${this.bufferedPercentage}%`,
      };
    },

    progress() {
      return (this.currentTime / this.duration) || 0;
    },

    progressBarStyle() {
      return {
        width: `${this.progress * 100}%`,
      };
    },

    /**
     * D-256 (A9). The range's value, 0-100.
     *
     * 🔑 This replaces a commented-out `scrubPosition()` that said "This should be used
     * if a input[type=range] is ever used" — so the shape was anticipated here and the
     * control simply never arrived.
     */
    position() {
      return this.progress * 100;
    },

    /**
     * 🔴 D-256 (A9). Whether a seek can be honoured AT ALL, and it is the guard the old
     * click handler did not have.
     *
     * `onProgressBarClick` checked only `playingLiveStream`, then did
     * `currentTime = percentage * this.duration`. Before metadata `duration` is NaN, so
     * the assignment threw — which is precisely why D-258's exemption comment said this
     * control "must NOT become a button (keyboard activation yields `currentTime = NaN`
     * and the setter throws)". A range with no bounds check would have re-armed that on
     * every arrow press, so the bound is what lets the control exist.
     */
    canSeek() {
      return !this.playingLiveStream
        && Number.isFinite(this.duration)
        && this.duration > 0;
    },

    /**
     * D-233 / D-255 (A9). What the transport button will DO.
     *
     * 🔑 Three names, because the handler has three branches, and the retry one is not a
     * nicety: in the `mediaFailed && !canPlay` window the visible text reads
     * "Unavailable — tap to retry" and the glyph is a Play triangle, so a name of "Play"
     * would be the only cue a screen-reader user gets and it would be the wrong one.
     * Keyed on exactly the condition `onPlayToggleButton` branches on.
     */
    playToggleLabel() {
      if (this.mediaFailed && !this.canPlay) return 'Retry loading audio';
      return this.isPlaying ? 'Pause' : 'Play';
    },

    /**
     * The fourth state, which has no branch in the handler: not loaded and not failed.
     * `onPlayToggleButton` early-returns, so the control does nothing and says nothing —
     * the shape v1's scrubber note calls "a control that lies". The visible readout is
     * "Buffering..."; this announces the same thing.
     */
    playToggleInert() {
      return !this.canPlay && !this.mediaFailed;
    },

    /**
     * D-256 (A9). Consumed by the `scrubberValueText` mixin.
     *
     * ⚠️ ELAPSED AND TOTAL, because that is what v2 DISPLAYS
     * (`{{ formattedTimePlayed }} / {{ formattedDuration }}`) — v4 announces elapsed and
     * remaining because that is what v4 shows, and v1 announces remaining only for the
     * same reason. An announced pair the sighted user cannot see is D-238's defect.
     */
    spokenPosition() {
      if (this.playingLiveStream) return null;

      const elapsed = formatDuration(this.currentTime);
      const total = formatDuration(this.duration);
      if (elapsed === null || total === null) return null;

      return `${elapsed} of ${total}`;
    },

    playToggleButton() {
      return this.isPlaying ? this.pauseButtonImage : this.playButtonImage;
    },

    playButtonImage() {
      return this.darkTheme ? playWhiteSvg : playGreySvg;
    },

    pauseButtonImage() {
      return this.darkTheme ? pauseWhiteSvg : pauseGreySvg;
    },

    formattedTimePlayed() {
      // TODO: If over an hour show hours format
      return formatDurationClock((this.currentTime || 0) * 1000);
    },

    formattedDuration() {
      // TODO: If over an hour show hours format
      return formatDurationClock(this.duration * 1000);
    },

    useM4a() {
      return this.audioFileSrc && this.audioFileSrc.includes('m4a');
    },
  },
  methods: {
    resetData() {
      this.loadedMediaData = false;
      this.isPlaying = false;
      this.canPlay = false;
      this.currentTime = undefined;
      this.duration = undefined;
      // D-138 — the failure latch MUST be cleared here too. Without it a latch armed
      // once stayed armed for the life of the component, across every episode and
      // stream change.
      this.mediaFailed = false;
      // 🔑 D-165 — and so must the retry latch, for D-138's exact reason. A
      // pending retry that survives a source change makes the NEXT episode's
      // ordinary first `canplay` report itself as the previous episode's retry
      // recovering — a permanent, silent inflation of the success rate the flag
      // was added to measure. Every latch this component has grown so far has
      // had this bug once; this one is born without it.
      this.retryPending = false;
    },
    // D-165 — the retry is reported through the SHARED reporter in `core/faro.js`,
    // not from here. v2, v3 and v4 all emit this event; three inline copies of an
    // attribute set is where a schema drifts, and this module already imports that
    // file for the other cross-version reporter. The reasoning about phases and
    // about why no url rides the event lives with the helper.
    reportRetry(phase) {
      reportPlayerRetry(phase, {
        playerVersion: 'v2',
        sourceKind: this.useM4a ? 'm4a' : 'hls',
        live: this.playingLiveStream,
      });
    },
    // D-165 — settle a pending retry exactly once, whichever way it went.
    settleRetry(phase) {
      if (!this.retryPending) return;
      this.retryPending = false;
      this.reportRetry(phase);
    },
    onAbort() {
      //
    },
    onCanPlay() {
      this.canPlay = true;
      this.settleRetry('recovered');
    },
    onDurationChange({ target }) {
      this.duration = target.duration;
    },
    onEmptied() {
      // 🔴 D-166 — an `emptied` must not UN-fail a failed load. On a ManagedMediaSource
      // engine (iOS 17+ Safari, the primary radio-widget platform) hls.js's give-up
      // teardown calls `load()`, which queues an `emptied` that lands AFTER
      // `notifyUnrecoverable`'s synthetic error has already armed `mediaFailed`. Clearing
      // the latch on that `emptied` disarmed the retry the give-up had just set up — and
      // because hls.js also removed the `<source>` (its appendSource cleanup) with nothing
      // left to fail and re-arm the latch, the player was left PERMANENTLY DEAD: the tap
      // hit `onPlayToggleButton`'s `!canPlay && !mediaFailed` early-return and did nothing.
      // MEASURED on webkit in the pinned Playwright image (real vendored engine, real 404
      // manifest): before, `srcAttr=null` / `sources=[]` / retry reached the network 0
      // times and the transport read "Buffering..." forever; after, the failure state
      // renders ("Unavailable — tap to retry") and the tap re-attaches through `retryLoad`,
      // which re-creates hls.js's source and reaches the network.
      //
      // The FAILURE LATCH is the only thing preserved. The watcher (a genuine new source)
      // and the retry tap still clear it, so D-138 holds for every real reset — only a
      // give-up's own teardown `emptied` is exempt. `resetData()`'s signature is left
      // untouched on purpose: `v2-transport-truth.spec.js` derives its give-up population
      // by locating `resetData() {` literally.
      const wasFailed = this.mediaFailed;
      this.resetData();
      if (wasFailed) {
        this.mediaFailed = true;
      }
    },
    onEnded() {
      this.isPlaying = false;
      this.$emit('episode-ended');
    },
    onError(event) {
      // eslint-disable-next-line no-console
      console.log(event);
      // D-133 — this used to be the log ALONE, which is why a failed load left the
      // play button dead: `onPlayToggleButton` early-returns on `!canPlay`, and
      // nothing here distinguished "failed" from "not ready yet". Bound on BOTH the
      // media element and the <source>, because a <source> failure does not bubble
      // (the engine fact D-105 measured: the src-IDL path reports at the element,
      // the source-children path reports at the source).
      // 🔴 D-138 — ignore the error chromium fires at a src-LESS `<source>`.
      // `audioFileSrc` is a String prop with no default, so before a stream is chosen
      // the template renders `<audio><source></audio>`; per the resource-selection
      // algorithm that is a failed candidate and chromium fires `error` AT THE SOURCE
      // unprompted — no `load()`, no user action. MEASURED on mount with no
      // interaction: exactly one source error, `hasSrcAttr:false`,
      // networkState 3 (NETWORK_NO_SOURCE).
      //
      // D-133 bound that event and so armed `mediaFailed` on EVERY pageview, which
      // misrouted the first tap in any `!canPlay` window into the retry branch — most
      // visibly on the live/HLS path, where `canPlay` stays false until hls.js buffers.
      const el = event && event.target;
      if (el && el.tagName === 'SOURCE' && !el.getAttribute('src')) return;

      this.mediaFailed = true;
      this.canPlay = false;
      this.isPlaying = false;
      // 🔴 D-160 — and `loadedMediaData`, which nobody derived. It is written only
      // here-adjacent: `resetData()` clears it and `onLoadedData()` sets it, so a
      // give-up mid-stream left it TRUE and the transport went on rendering its
      // LIVE/time branch for a destroyed stream. The population is `resetData()`'s
      // own list — every member of it now has a stated give-up behaviour, derived and
      // checked in `test/unit-vue/v2-transport-truth.spec.js` rather than listed here
      // where it would rot.
      //
      // 🪤 D-173 — `currentTime`/`duration` stay KEPT, but the reason I first gave was
      // FALSE and refuted 200-odd lines up in this same file. It said they "render only
      // inside the branch this clears, so they cannot reach the listener". They also
      // feed `progress` -> the `progressBarStyle` computed, bound to
      // `.post-webwidget-v2-player-audio-player-progress-bar__played` at :38 — a div
      // OUTSIDE the `loadedMediaData` branch, rendered on every mount. So they reach
      // the listener on every pageview. Same shape as D-162: a claim written a short
      // scroll from its own refutation.
      //
      // 🔑 The classification survives on a DIFFERENT and true argument. The progress
      // bar showing where playback stopped is HONEST — the media did fail there, and
      // snapping it to 0 would imply the episode never played. And on the live path the
      // question does not arise: `duration` is Infinity or NaN for a live stream, so
      // `progress` is `|| 0` and the bar is already empty. Kept, for the reason that is
      // actually true.
      this.loadedMediaData = false;
      // D-165 — if this failure followed a retry, that is the retry's outcome.
      // Reported LAST, after the state above is settled, so a throw in telemetry
      // could never cost the component its give-up handling.
      this.settleRetry('failed');
    },
    onLoadedData({ target }) {
      this.loadedMediaData = true;
      this.duration = target.duration;
    },
    onPause() {
      this.isPlaying = false;
      this.$emit('pause');
    },
    onPlay() {
      this.canPlay = true;
      this.isPlaying = true;
      this.$emit('play');
    },
    onPlaying() {
      //
    },
    onProgress() {
      // this.buffered = target.buffered.end(0);
    },
    /**
     * D-256 (A9). Replaces `onProgressBarClick`, which read `event.offsetX` and so could
     * only ever be driven by a pointer.
     */
    onSeek(event) {
      if (!this.canSeek) {
        // D-276: not seekable (live stream / before metadata). The <input> has already
        // moved its thumb and implicit `aria-valuenow` to where the user dragged; snap it
        // back to the real playback position so the announced value does not drift.
        const range = event.target;
        range.value = this.position;
        return;
      }
      // Arms an immediate announcement for the position that ARRIVES, not the one being
      // left — the mixin's 🔴 has the measurement.
      this.markUserSeek();
      this.$refs.audioPlayer.currentTime = (Number(event.target.value) / 100) * this.duration;
    },
    onSeeked() {
      //
    },
    onSeeking() {
      //
    },
    onStalled() {
      //
    },
    onSuspend() {
      //
    },
    onTimeUpdate(event) {
      this.currentTime = event.target.currentTime;
    },
    onVolumeChange() {
      //
    },
    onWaiting() {
      //
    },
    // 🔴 D-165 — the retry has to take the SAME branch the attach took, or it
    // hands the element to an engine that does not own it.
    //
    // D-133 made the retry `audioPlayer.load()` — a bare NATIVE load — and D-148
    // made that load able to reach the network instead of dying on a revoked
    // blob. Neither made it able to PLAY, because every non-m4a source v2 hands
    // the element is an HLS manifest and a native decoder cannot read one.
    // MEASURED: chromium answers `'maybe'` to
    // `canPlayType('application/vnd.apple.mpegurl')` and then fails with
    // MEDIA_ERR_SRC_NOT_SUPPORTED anyway, so the retry ended exactly where it
    // started, one round-trip later. Safari and iOS have native HLS and DO
    // recover — which is why this survived hand-testing.
    //
    // 🔑 THE GATE IS `useM4a`, NOT `playingLiveStream` — but read which `useM4a`.
    // 🪤 `useM4a` IS SPELLED THE SAME IN NINE DECLARATIONS ACROSS FIVE DISTINCT
    // ROLES, and an earlier version of this comment ran two of them together. The one
    // used here is THIS component's own computed, `audioFileSrc.includes('m4a')` — a
    // sniff of the src the watcher was handed. It is not a prop (this component has
    // none by that name: see `props` above). The other four roles: the v2 prop chain
    // (V2Player -> NowPlayingSection), the v1 prop chain (podcast_list -> podcast),
    // `app.vue`'s `v3PlayerConfigs.useM4a`, and `parseCoreConfig`'s output field —
    // whose QUERY-PARAM KEY is `use_m4a` but whose FIELD is `useM4a`. Grep for the
    // wrong spelling and you will conclude the chain is not there.
    //
    // ⚠️ The population argument this comment used to make is FALSE, and the
    // correction is D-220 (MEASURED 2026-09-06). It read: `episodeAudioFileSrc`
    // returns `m4aUrl` when the embed sets `useM4a` "which DEFAULTS OFF" or
    // `m3u8Url` when it does not, so an ordinary on-demand episode in a default
    // embed is a `.m3u8`. The default was real but never arrived: `parseCoreConfig`
    // USED TO default `useM4a` false and `app.vue` USED TO copy it to
    // `v2Player.useM4a` — 🪤 D-220 DELETED BOTH on 2026-09-06, precisely because
    // nothing read the result. What has always decided it is the literal
    // `:use-m4a="true"` bound on the `<v2-player>` mount, as it has since `1a61585`
    // (2020-02-21). So
    // `NowPlayingSection.useM4a` is ALWAYS true, its `m3u8Url` branch is
    // unreachable in the shipped app, and no on-demand episode reaches this
    // component as a `.m3u8`. `playingLiveStream` is `canPlayLiveStream` passed
    // straight down, so today the two gates partition the traffic IDENTICALLY.
    //
    // 🔑 What survives, and is the actual reason for the gate: it asks the SAME
    // question the `audioFileSrc` watcher asks, so the retry cannot disagree
    // with the attach about which engine owns the element. That property holds
    // whatever the src turns out to be — including if the prop is ever wired to a
    // real value instead of the current hardcoded `true`, which would make the
    // `.m3u8` population real. (D-220 resolved it by DELETING the parse; this
    // property does not depend on which way that went.) A gate on
    // `playingLiveStream` would be a second opinion about the same fact, and free
    // to drift from the first.
    retryLoad() {
      const audioPlayer = this.$refs.audioPlayer;
      if (this.useM4a) {
        // The m4a bypass is native on the way in, so it is native on the way
        // back: `load()` re-runs resource selection on the src the watcher set.
        audioPlayer.load();
        return;
      }
      // Stop-before-(re)attach, exactly as the watcher does. `attachLiveStream`
      // tears down internally too, but going through the same pair here keeps
      // one shape for "put this source on this element".
      detachLiveStream(audioPlayer);
      // 🔑 `autoplay: true`, and ONLY here. The watcher forwards the `autoplay`
      // PROP because it fires on a source change — not a user gesture, and
      // subject to autoplay policy. This path is a click on the play control:
      // the listener has asked for audio, and a re-attach that leaves silence
      // would make them tap a second time for no reason they can see. A rejected
      // `play()` is already caught and reported inside the engine (D-151/D-207),
      // so the optimistic call cannot surface as an unhandled rejection.
      try {
        attachLiveStream(audioPlayer, this.audioFileSrc, { autoplay: true });
      } catch (e) {
        // Parity with the watcher's belt-and-suspenders catch; the engine is
        // already throw-safe.
      }
    },
    onPlayToggleButton() {
      if (!this.canPlay) {
        // D-133 — a tap after a failed load used to do NOTHING, forever. Retry the
        // load instead: the control the listener can see must be able to act. The
        // latch is cleared so a second failure re-arms rather than looping.
        if (this.mediaFailed) {
          this.mediaFailed = false;
          this.retryPending = true;
          this.reportRetry('attempted');
          this.retryLoad();
        }
        return;
      }

      if (this.$refs.audioPlayer.paused) {
        Promise.resolve(this.$refs.audioPlayer.play())
        .then(() => {
          this.isPlaying = true;
        })
        .then(null, (error) => {
          // 🔴 D-203 — thenable-safe WITHOUT collapsing to `.then(f, h)`.
          //
          // The chained form was kept on purpose: in `.then(f).catch(h)` the handler also
          // catches a throw from `f`, and collapsing to `.then(f, h)` silently drops that.
          // But `.play()` may return a host-patched thenable with no `.catch` (D-176), so
          // the chain could not be left as it was.
          //
          // 🪤 `.play().then(f).then(null, h)` DOES NOT FIX IT. On a minimal thenable,
          // `T.then(f)` returns whatever that `then` returns — possibly `undefined` — and
          // `.then(null, h)` on undefined throws the same TypeError one link further down.
          // Wrapping FIRST is what makes the rest of the chain native and therefore safe.
          //
          // 🔑 AND ONCE WRAPPED, `.catch(h)` WOULD BE EQUALLY SAFE -- `.catch(h)` IS
          // DEFINED as `.then(undefined, h)`, so on the native promise the wrap returns
          // the two forms are identical. `.then(null, h)` is used here for consistency
          // with the rest of the tree, a convention pinned since D-176 by
          // test/unit/play-rejection-handler-shape.spec.js. Do NOT read this block as
          // "`.catch` is unsafe on a native promise" -- it is not, and an earlier
          // version of this comment implied exactly that.
          // 🔴 D-146 — v2 reports the NotAllowed measurement; it was v4-only.
          // ⚠️ WITHOUT a station, deliberately. This component has no `station` prop —
          // the value lives two parents up in NowPlayingSection — and threading it
          // through NowPlayingDetail for an OPTIONAL attribution field is more surface
          // than the attribution is worth. An unattributed count is still a count,
          // where today there is none at all. If multi-station v2 embeds ever need the
          // split (the MED-5 concern), pass the prop then.
          reportNotAllowedIfApplicable(error);
          // eslint-disable-next-line no-console
          console.error(error);
        });
      } else {
        this.$refs.audioPlayer.pause();
      }
    },
  },
  mounted() {
    this.$emit('ready', this.$refs.audioPlayer);
  },
  beforeUnmount() {
    // LP-4/MR-78 (fidelity fold, closes PS-12): the Vue-1 original had no
    // teardown, so a torn-down player left any live hls.js session attached.
    // Mirror the v4 Player sibling and detach the live engine on unmount
    // (detachLiveStream is null-safe + throw-safe). v2 has no pauseMedia wrapper
    // — the <audio> is destroyed with the component — so the detach IS the
    // teardown parity.
    detachLiveStream(this.$refs.audioPlayer);
  },
  watch: {
    audioFileSrc(newStreamSrc) {
      // The master guard that early-returned when the Viblast global was absent was intentionally dropped: the shared engine helper is throw-safe.
      this.resetData();

      const audioPlayer = this.$refs.audioPlayer;
      // Stop-before-(re)attach: always tear down any live session first (was
      // the Viblast stop() call the setup() was always preceded by).
      // detachLiveStream is idempotent + throw-safe.
      detachLiveStream(audioPlayer);

      if (this.useM4a) {
        // m4a bypass stays NATIVE — hls.js must never attach for m4a (the old
        // note: "Viblast doesn't support m4a playback").
        audioPlayer.setAttribute('src', newStreamSrc);
        // 🔴 D-176 — `.then(null, handler)`, not `.catch(handler)`: a patched `play()`
        // may return a thenable without `.catch`, and calling it throws TypeError at the
        // moment playback already failed. See startPlayback in hls_engine.js.
        audioPlayer.play()
          .then(null, (e) => {
            // D-146 — the m4a bypass rejects the same way.
            reportNotAllowedIfApplicable(e);
            // eslint-disable-next-line no-console
            console.error(e);
          });
        return;
      }

      // Live HLS via the shared engine (LP-H/MR-75), replacing viblast.setup().
      // The `key` (Viblast license) + `forceHEAAC` drop — no hls.js equivalent
      // (HE-AAC is handled by hls.js's transmuxer). attachLiveStream is already
      // throw-safe (it swallows the iOS quirk the old setup was try/caught for);
      // the try/catch is kept for belt-and-suspenders parity with the original.
      try {
        attachLiveStream(audioPlayer, newStreamSrc, { autoplay: this.autoplay });
      } catch (e) {
        //
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/variables-v2';
.post-webwidget-v2-player-audio-player {
  &-controls {
    display: flex;
    width: 100%;
    align-items: center;

    &-left-side {
      margin-right: 1em;
    }

    &-right-side {
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: center;
    }


    &__play-toggle {
      cursor: pointer;

      // D-233 (A9): UA reset for the promotion to `<button>`.
      //
      // 🔑 `display: block` and `font: inherit`, deliberately NOT `display: flex`. This
      // was a block `<div>` wrapping an inline 30x30 `<img>`, so its height is a LINE
      // BOX — the image plus the inherited font's leading, not 30px. `display: flex`
      // would collapse that leading and shorten the control, and `-controls` is
      // `align-items: center`, so the whole transport row would shift. `font: inherit`
      // carries `line-height`, which is what keeps the leading identical; a UA button's
      // 13.333px Arial would have changed it.
      //
      // 🪤 `text-align: left` because a UA button centres its content. Harmless while the
      // box is exactly the image's width, and wrong the moment it is not.
      appearance: none;
      -webkit-appearance: none;
      display: block;
      margin: 0;
      padding: 0;
      border: 0;
      background: none;
      color: inherit;
      font: inherit;
      text-align: left;

      // R11 (a11y): the play/pause glyph renders 30x33 on a phone, under the
      // plan's 44px touch-target figure (§7.4). Expand the HIT AREA only —
      // transparent and out of flow, so the control bar is pixel-identical.
      // It is the only target in `-left-side`, which is separated from the
      // progress column by a 1em margin, so a 44px box has nothing to steal
      // from. @media(max-width)-gated ONLY.
      // ⚠️ NOTE THE CEILING: this is v2's existing $mobileBreakpoint (400px),
      // not the 480px every other R11 block uses — deliberately, so v2 extends
      // its own established ladder rather than layering a second breakpoint
      // (RULE 3). The cost is a real 401-480px gap: on a 414px iPhone Plus or a
      // 428px Pro Max column, v3/v4 transport buttons get their grown hit areas
      // and v2's play toggle stays at its base size. Accepted; recorded so it is
      // a known gap rather than an oversight.
      @media screen and (max-width: $mobileBreakpoint) {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 44px;
          height: 44px;
        }
      }
    }
  }

  &-progress-text {
    display: flex;
    justify-content: space-between;
    width: 100%;
    font-size: 0.7em;
  }

  &-progress-bar {
    margin-bottom: 0.5em;
    position: relative;
    cursor: pointer;
    width: 100%;
    height: 10px;
    background: $audioPlayerProgressBackground;
    border-radius: 2.5px; // half of $borderRadius
    box-shadow: 0px 0px 1px $borderColor;

    /*
    &__buffered {
      position: absolute;
      height: 100%;
      background: $audioPLayerBufferedProgressBackground;
      border-radius: 2.5px;
    }
    */

    &__played {
      pointer-events: none; // Neccessary to ignore click so the parent div can catch the event and determine where to seek
      position: absolute;
      z-index: 2;
      height: 100%;
      background: $audioPlayerPlayedProgressBackground;
      border-radius: 2.5px; // half of $borderRadius
    }

    // 🔴 D-256 (A9): the real control, laid transparently over the bar it drives.
    //
    // Every declaration here exists to make the range PAINT NOTHING — the div above is
    // still the whole visual, so the 36 v2 goldens see what they always saw. The track,
    // both vendor thumbs and the Firefox progress fill are each explicitly transparent:
    // `appearance: none` alone leaves a UA-painted track in Blink.
    //
    // 🔑 A TRANSPARENT THUMB IS CORRECT HERE, not a compromise. v2 has never drawn a
    // playhead knob — the fill's leading edge IS the position indicator — so a visible
    // thumb would be this MR inventing a look. The focus ring from `a11y.css` is what
    // makes the control findable, and it lands on this element.
    //
    // 🔑 `z-index: 3` puts it above `__played` for HIT-TESTING as well as paint order.
    // `__played` is already `pointer-events: none`, so this is belt and braces — but the
    // ordering is what the comment above `__played` was always relying on.
    &__range {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 3;
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      appearance: none;
      -webkit-appearance: none;
      background: transparent;
      cursor: pointer;

      &::-webkit-slider-runnable-track {
        background: transparent;
        border: 0;
      }

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 10px;
        height: 10px;
        border: 0;
        background: transparent;
      }

      &::-moz-range-track {
        background: transparent;
        border: 0;
      }

      &::-moz-range-progress {
        background: transparent;
      }

      &::-moz-range-thumb {
        width: 10px;
        height: 10px;
        border: 0;
        background: transparent;
      }
    }
  }

  &--dark-theme {
    .post-webwidget-v2-player-audio-player {
      &-progress-bar {
        background: $darkThemeAudioPlayerProgressBackground;
        box-shadow: 0px 0px 1px $darkThemeBorderColor;
        &__played {
          background: $darkThemeAudioPlayerPlayedProgressBackground;
        }
      }
    }
  }
}
</style>
